import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Panel } from "@/components/ui/Panel";
import { Button } from "@/components/ui/Button";
import { Field, Input, Select, Textarea } from "@/components/ui/Form";
import { MonoText } from "@/components/ui/CopyButton";
import { Badge } from "@/components/ui/Badge";
import { LoadingState, ErrorState } from "@/components/ui/States";
import { formatIDR, formatDateTime, formatDuration } from "@/lib/format";
import { usesStockPool } from "@ag5r-store/shared";
import type { DeliveryType } from "@ag5r-store/shared";

interface Variant {
  id: string;
  product_id: string;
  name: string;
  duration_days: number;
  price: number;
}

interface ProductOption {
  id: string;
  name: string;
  product_code: string;
  /** Hanya SERIAL_KEY & ACCOUNT_CREDENTIAL yang mengambil dari pool stok. */
  delivery_type: DeliveryType;
}

export function AdminInventoryPage() {
  const { user, loading: authLoading } = useAuth();
  const [products, setProducts] = useState<ProductOption[]>([]);
  const [productId, setProductId] = useState("");
  const [variants, setVariants] = useState<Variant[]>([]);
  const [variantId, setVariantId] = useState("");
  const [payloads, setPayloads] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [stockInfo, setStockInfo] = useState<{ variant_id: string; stock_available: number } | null>(null);
  const [pool, setPool] = useState<Awaited<ReturnType<typeof api.listPool>>>([]);
  const [poolLoading, setPoolLoading] = useState(false);

  // Load products
  const loadProducts = useCallback(async () => {
    try {
      const data = await api.listProductsAdmin();
      setProducts(
        data.map((p) => ({
          id: p.id,
          name: p.name,
          product_code: p.product_code,
          // Dipakai untuk menyaring: hanya ACCOUNT_CREDENTIAL yang punya stok.
          delivery_type: p.delivery_type,
        })),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load products");
    }
  }, []);

  useEffect(() => {
    if (!authLoading && user?.role === "owner") loadProducts();
  }, [user, authLoading, loadProducts]);

  // Load variants saat productId berubah
  useEffect(() => {
    if (!productId) {
      setVariants([]);
      setVariantId("");
      return;
    }
    api.listVariantsAdmin(productId)
      .then((v) => {
        const list = v as Variant[];
        setVariants(list);
        setVariantId(list[0]?.id ?? "");
      })
      .catch((e) => setError(e.message));
  }, [productId]);

  // Refresh stock saat variantId berubah
  const loadPool = useCallback(async (vid: string) => {
    if (!vid) return;
    setPoolLoading(true);
    try {
      setPool(await api.listPool(vid));
    } catch {
      // Daftar stok gagal dimuat tidak boleh menjatuhkan form injeksi.
      setPool([]);
    } finally {
      setPoolLoading(false);
    }
  }, []);

  useEffect(() => {
    if (variantId) void loadPool(variantId);
    else setPool([]);
  }, [variantId, loadPool]);

  const refreshStock = useCallback(async () => {
    if (!variantId) {
      setStockInfo(null);
      return;
    }
    try {
      const s = await api.getPoolStats(variantId);
      setStockInfo(s);
    } catch {
      setStockInfo(null);
    }
  }, [variantId]);

  useEffect(() => {
    refreshStock();
  }, [refreshStock]);

  async function handleSubmit() {
    if (!variantId) {
      setError("Select a variant first");
      return;
    }
    const lines = payloads
      .split("\n")
      .map((s) => s.trim())
      .filter(Boolean);
    if (lines.length === 0) {
      setError("No payloads to insert");
      return;
    }

    setSubmitting(true);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.bulkInsertPool({
        variant_id: variantId,
        payloads: lines,
      });
      setSuccess(`Inserted ${result.inserted} entries into pool`);
      setPayloads("");
      await refreshStock();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Bulk insert failed");
    } finally {
      setSubmitting(false);
    }
  }

  if (authLoading) return <LoadingState />;
  if (!user || user.role !== "owner") {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <MonoText size="sm" color="status-failed">[ ACCESS_DENIED ]</MonoText>
      </div>
    );
  }

  const selectedVariant = variants.find((v) => v.id === variantId);
  const lineCount = payloads.split("\n").filter((s) => s.trim()).length;

  return (
    <div className="max-w-5xl mx-auto px-4 py-10">
      <div className="mb-8">
        <h1 className="section-heading">POOL_VAULT</h1>
        <h2 className="mt-3 font-sans text-3xl text-ink-primary">Inventory Injector</h2>
        <MonoText size="xs" color="ink-muted" className="block mt-1">
          Masukkan serial key atau akun yang akan dikirim otomatis ke pembeli.
          Satu baris = satu stok.
        </MonoText>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        {/* Form */}
        <Panel>
          <div className="space-y-4">
            <Field
              label="PRODUK"
              required
              hint="Serial key dan akun sama-sama diambil dari stok ini."
            >
              <Select value={productId} onChange={(e) => setProductId(e.target.value)}>
                <option value="">— pilih produk —</option>
                {/* STATIC_LINK_ONLY & AFFILIATE tidak punya stok — produknya
                    sebuah tautan, bukan barang yang habis. */}
                {products
                  .filter((p) => usesStockPool(p.delivery_type))
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      [{p.product_code}] {p.name}
                    </option>
                  ))}
              </Select>
            </Field>

            {products.length > 0 &&
              products.filter((p) => usesStockPool(p.delivery_type)).length === 0 && (
                <div className="border border-status-pending p-3">
                  <MonoText size="xs" color="status-pending">
                    Semua produk Anda bertipe tautan (STATIC_LINK_ONLY / AFFILIATE),
                    yang tidak memakai stok.
                  </MonoText>
                </div>
              )}

            {variants.length > 0 && (
              <Field label="VARIANT" required>
                <Select value={variantId} onChange={(e) => setVariantId(e.target.value)}>
                  {variants.map((v) => (
                    <option key={v.id} value={v.id}>
                      {v.name} — {formatIDR(v.price)} ({formatDuration(v.duration_days)})
                    </option>
                  ))}
                </Select>
              </Field>
            )}

            <Field
              label="PAYLOADS"
              required
              hint={`One credential per line. Format: user:password. Currently: ${lineCount} line${lineCount !== 1 ? "s" : ""}.`}
            >
              <Textarea
                value={payloads}
                onChange={(e) => setPayloads(e.target.value)}
                rows={12}
                placeholder={`premium001@vault.io:secret123\npremium002@vault.io:secret456\npremium003@vault.io:secret789`}
                className="text-xs"
              />
            </Field>

            {error && (
              <div className="border border-status-failed p-3">
                <MonoText size="xs" color="status-failed">! {error}</MonoText>
              </div>
            )}
            {success && (
              <div className="border border-terminal p-3">
                <MonoText size="xs" color="terminal">✓ {success}</MonoText>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button
                variant="primary"
                onClick={handleSubmit}
                loading={submitting}
                disabled={!variantId || lineCount === 0}
              >
                INJECT {lineCount > 0 ? `${lineCount} ENTRIES` : "POOL"}
              </Button>
            </div>
          </div>
        </Panel>

        {/* Sidebar: stock info */}
        <Panel title="VARIANT_STOCK">
          {stockInfo ? (
            <div className="space-y-3">
              <div>
                <MonoText size="xs" color="ink-dim">VARIANT_ID</MonoText>
                <MonoText size="xs" color="ink-primary" className="block break-all">
                  {stockInfo.variant_id}
                </MonoText>
              </div>
              <div>
                <MonoText size="xs" color="ink-dim">STOCK_AVAILABLE</MonoText>
                <div
                  className={`font-mono text-3xl tabular-nums mt-1 ${
                    stockInfo.stock_available > 0 ? "text-terminal" : "text-status-failed"
                  }`}
                >
                  {stockInfo.stock_available}
                </div>
              </div>
              {selectedVariant && (
                <>
                  <div className="border-t border-border pt-3">
                    <MonoText size="xs" color="ink-dim">DURATION</MonoText>
                    <MonoText size="xs" color="ink-primary" className="block">
                      {formatDuration(selectedVariant.duration_days)}
                    </MonoText>
                  </div>
                  <div>
                    <MonoText size="xs" color="ink-dim">PRICE</MonoText>
                    <MonoText size="xs" color="terminal" className="block">
                      {formatIDR(selectedVariant.price)}
                    </MonoText>
                  </div>
                </>
              )}
              <div className="border-t border-border pt-3">
                <MonoText size="xs" color="ink-dim">LAST_REFRESH</MonoText>
                <MonoText size="xs" color="ink-muted" className="block">
                  {formatDateTime(new Date().toISOString())}
                </MonoText>
              </div>
              <Button variant="ghost" onClick={refreshStock} block>
                [ REFRESH ]
              </Button>
            </div>
          ) : (
            <MonoText size="xs" color="ink-dim">Pilih varian untuk melihat stok.</MonoText>
          )}
        </Panel>
      </div>

      {/* Riwayat pemakaian stok — siapa memakai entri mana, lewat pesanan apa. */}
      {variantId && (
        <div className="mt-6">
          <Panel title="ISI & RIWAYAT STOK">
            <div className="flex items-center justify-between mb-3">
              <MonoText size="xs" color="ink-muted">
                {pool.length} entri · {pool.filter((e) => !e.is_claimed).length} belum terpakai
              </MonoText>
              <Button variant="ghost" onClick={() => void loadPool(variantId)}>
                [ MUAT ULANG ]
              </Button>
            </div>

            {poolLoading ? (
              <MonoText size="xs" color="ink-dim">Memuat…</MonoText>
            ) : pool.length === 0 ? (
              <MonoText size="xs" color="ink-dim">
                Belum ada kredensial untuk varian ini.
              </MonoText>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-2 py-2">KREDENSIAL</th>
                      <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-2 py-2">STATUS</th>
                      <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-2 py-2">PESANAN</th>
                      <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-2 py-2">DIPAKAI</th>
                      <th className="text-left font-mono text-2xs uppercase tracking-widest text-ink-muted px-2 py-2">DIMASUKKAN</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pool.map((e) => (
                      <tr key={e.id} className="border-b border-border last:border-b-0">
                        <td className="px-2 py-2 font-mono text-xs text-ink-primary break-all">
                          {e.secret_preview}
                        </td>
                        <td className="px-2 py-2">
                          <Badge variant={e.is_claimed ? "expired" : "active"}>
                            {e.is_claimed ? "TERPAKAI" : "TERSEDIA"}
                          </Badge>
                        </td>
                        <td className="px-2 py-2 font-mono text-xs text-ink-muted break-all">
                          {e.claimed_order_id ?? "—"}
                        </td>
                        <td className="px-2 py-2 font-mono text-xs text-ink-muted">
                          {e.claimed_at ? formatDateTime(e.claimed_at) : "—"}
                        </td>
                        <td className="px-2 py-2 font-mono text-xs text-ink-dim">
                          {formatDateTime(e.created_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <MonoText size="xs" color="ink-dim" className="block mt-3">
              Password sengaja disamarkan di server — kredensial yang belum terpakai
              tidak perlu meninggalkan Worker dalam bentuk utuh.
            </MonoText>
          </Panel>
        </div>
      )}
      <div className="hidden">
      </div>
    </div>
  );
}